import {
  ChangeDetectionStrategy,
  Component,
  signal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * Content-projection wrapper around a `mat-form-field` input that holds a
 * secret value (API key, dev password, integration token — anything that's
 * not a real account password). Same visual treatment as `zef-password-field`
 * (eye toggle + appended icon), but the wrapped input MUST stay `type="text"`
 * — masking is done via CSS so Chrome's built-in password manager never sees
 * a `type="password"` field and therefore never offers to save credentials
 * against the user's logged-in account.
 *
 * Background: Chrome (per chromium-dev policy) **ignores** every
 * `autocomplete` value on `type="password"` and `data-*-ignore` only
 * affects third-party extensions like 1Password/LastPass, not Chrome's
 * built-in manager. The only reliable suppression is to not use
 * `type="password"`. This component encapsulates that workaround so callers
 * keep a clean show/hide UX without each one re-implementing the mask.
 *
 * Usage:
 * ```html
 * <mat-form-field appearance="outline" color="accent">
 *   <mat-label>API Key</mat-label>
 *   <zui-secret-field>
 *     <input matInput type="text" autocomplete="off"
 *            name="my-key"
 *            data-1p-ignore="true" data-lpignore="true"
 *            [ngrxFormControlState]="..." />
 *   </zui-secret-field>
 *   <mat-hint>Stored as a secret env var on the service</mat-hint>
 * </mat-form-field>
 * ```
 */
@Component({
  selector: 'zui-secret-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ MatIconModule ],
  template: `
    <div class="__wrap" [class.is-masked]="!reveal()">

      <mat-icon
        matSuffix
        fontSet="material-icons-outlined"
        class="__visibility-icon"
        role="button"
        tabindex="0"
        [attr.aria-label]="reveal() ? 'Hide value' : 'Reveal value'"
        (click)="toggle()"
        (keydown.enter)="toggle()"
        (keydown.space)="toggle()">
        {{ reveal() ? 'visibility_off' : 'visibility' }}
      </mat-icon>

      <div class="__input">
        <ng-content></ng-content>
      </div>

    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    .__wrap {
      position: relative;
    }

    .__input {
      padding-right: 36px;
    }

    // Apply the CSS mask via host-side class — covers Chromium/Edge/Safari
    // natively and Firefox 119+ via the -webkit- alias. Letter-spacing
    // tightens the dot column for legibility at small font sizes.
    .__wrap.is-masked ::ng-deep input {
      -webkit-text-security: disc;
      letter-spacing: 0.05em;
    }

    .__visibility-icon {
      position: absolute;
      top: -4px;
      right: 0;
      cursor: pointer;
      user-select: none;
      outline: none;
    }
  `
})
export class ZuiSecretFieldComponent {
  reveal = signal(false);

  toggle(): void {
    this.reveal.update((v) => !v);
  }
}
